import { useEffect, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { cn } from '@/lib/cn'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { Structure } from './StructureCard'

export interface StructureFormData {
  name: string
  identifierCode: string
  type: string
  region: string
  district: string
  latitude: string
  longitude: string
}

interface StructureFormModalProps {
  open: boolean
  onClose: () => void
  onSubmit: (data: StructureFormData) => void
  isSubmitting: boolean
  structure?: Structure | null
}

const STRUCTURE_TYPES = [
  { value: 'hopital', label: 'Hôpital' },
  { value: 'centre-sante', label: 'Centre de santé' },
  { value: 'clinique', label: 'Clinique' },
  { value: 'poste-sante', label: 'Poste de santé' },
  { value: 'prov', label: 'Établissement (FHIR)' },
]

export function StructureFormModal({
  open,
  onClose,
  onSubmit,
  isSubmitting,
  structure,
}: StructureFormModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const isEditing = !!structure

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<StructureFormData>()

  // Reset form when modal opens/closes or structure changes
  useEffect(() => {
    if (open) {
      if (structure) {
        reset({
          name: structure.name,
          identifierCode: structure.identifierCode,
          type: structure.type,
          region: structure.region,
          district: structure.district,
          latitude: structure.latitude != null ? String(structure.latitude) : '',
          longitude: structure.longitude != null ? String(structure.longitude) : '',
        })
      } else {
        reset({
          name: '',
          identifierCode: '',
          type: 'hopital',
          region: 'Dakar',
          district: '',
          latitude: '',
          longitude: '',
        })
      }
    }
  }, [open, structure, reset])

  // Close on Escape key
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose()
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/40" />

      {/* Modal */}
      <div
        className={cn(
          'relative w-full max-w-lg bg-white rounded-xl shadow-xl',
          'max-h-[90vh] overflow-y-auto'
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-neutral-200">
          <h2 className="text-lg font-semibold text-neutral-900">
            {isEditing ? 'Modifier la structure' : 'Ajouter une structure'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-neutral-400 hover:bg-neutral-100 hover:text-neutral-600 transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="px-6 py-5 space-y-4">
          <Input
            label="Nom de la structure"
            required
            placeholder="Ex : Hôpital Principal de Dakar"
            error={errors.name?.message}
            {...register('name', { required: 'Le nom est requis' })}
          />

          <Input
            label="Code identifiant"
            required
            placeholder="Ex : HPD-001"
            error={errors.identifierCode?.message}
            {...register('identifierCode', { required: 'Le code est requis' })}
          />

          {/* Type select */}
          <div className="w-full">
            <label htmlFor="structure-type" className="block text-sm font-medium text-neutral-700 mb-1">
              Type de structure
              <span className="text-error-500 ml-0.5">*</span>
            </label>
            <select
              id="structure-type"
              className={cn(
                'block w-full rounded-md border px-3 py-2 text-sm',
                'transition-colors duration-200',
                'focus:outline-none focus:ring-2 focus:ring-offset-0',
                errors.type
                  ? 'border-error-300 text-error-900 focus:ring-error-500 bg-error-50'
                  : 'border-neutral-300 text-neutral-900 focus:ring-primary-500 focus:border-primary-500'
              )}
              {...register('type', { required: 'Le type est requis' })}
            >
              {STRUCTURE_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
            {errors.type?.message && (
              <p className="mt-1 text-sm text-error-600">{errors.type.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Région"
              placeholder="Ex : Dakar"
              error={errors.region?.message}
              {...register('region')}
            />
            <Input
              label="District"
              placeholder="Ex : Plateau"
              error={errors.district?.message}
              {...register('district')}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Latitude"
              placeholder="Ex : 14.6928"
              type="number"
              step="any"
              error={errors.latitude?.message}
              {...register('latitude', {
                validate: (v) => {
                  if (!v) return true
                  const n = parseFloat(v)
                  if (isNaN(n)) return 'Nombre invalide'
                  if (n < -90 || n > 90) return 'Entre -90 et 90'
                  return true
                },
              })}
            />
            <Input
              label="Longitude"
              placeholder="Ex : -17.4467"
              type="number"
              step="any"
              error={errors.longitude?.message}
              {...register('longitude', {
                validate: (v) => {
                  if (!v) return true
                  const n = parseFloat(v)
                  if (isNaN(n)) return 'Nombre invalide'
                  if (n < -180 || n > 180) return 'Entre -180 et 180'
                  return true
                },
              })}
            />
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t border-neutral-200">
            <Button type="button" variant="outline" size="md" onClick={onClose} disabled={isSubmitting}>
              Annuler
            </Button>
            <Button type="submit" variant="primary" size="md" isLoading={isSubmitting}>
              {isEditing ? 'Mettre à jour' : 'Créer'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}
